import type {
  GetSeedingProposalDetailResponseData,
  GetSeedingRecruitmentDetailResponseData,
} from '@musinsa/apis-curator';

import {
  SEEDING_PROPOSAL_SCREEN_STATE,
  type SeedingProposalScreenState,
} from './get-seeding-proposal-detail.dto';
import {
  SEEDING_RECRUITMENT_SCREEN_STATE,
  type SeedingRecruitmentScreenState,
} from './get-seeding-recruitment-detail.dto';

/**
 * BE 날짜 포맷 ("YYYY-MM-DD HH:mm:ss" — KST, timezone 표기 없음) 을 ms epoch 로 변환.
 * Date 생성자는 이 포맷을 환경에 따라 local 시간으로 해석하는 경우가 있어,
 * 명시적으로 ISO `T` + `+09:00` offset 으로 치환한다.
 */
export const parseKstDateTime = (value: string): number =>
  new Date(`${value.replace(' ', 'T')}+09:00`).getTime();

const isAllSoldOut = (
  goods:
    | GetSeedingRecruitmentDetailResponseData['goods']
    | GetSeedingProposalDetailResponseData['goods'],
): boolean => goods.length > 0 && goods.every(({ soldOut }) => soldOut);

/**
 * slotNo(상품그룹/아더컬러) 단위 품절 판정 — 한 그룹이라도 옵션이 **전량** 품절이면 true.
 * (제안형 진행불가 게이트용)
 *
 * 그룹 내 일부 색상만 품절인 경우는 그 그룹을 품절로 보지 않는다 (해당 옵션만 비활성, 진행 가능).
 * 단일 그룹에서는 그 그룹 `every(soldOut)` 와 동치이고, 다중 그룹에서는
 * "한 그룹 전량품절 + 다른 그룹 가용" 도 진행불가로 처리한다.
 * 뷰(`SeedingProposeDetailPageView`)의 그룹 품절 기준(`group.items.every(soldOut)`)과 정렬된다.
 */
const isAnySlotGroupSoldOut = (
  goods:
    | GetSeedingRecruitmentDetailResponseData['goods']
    | GetSeedingProposalDetailResponseData['goods'],
): boolean => {
  if (goods.length === 0) return false;
  // slotNo -> 그 그룹이 (지금까지 본 옵션 기준) 전량품절인가
  const soldOutBySlot = new Map<number, boolean>();
  for (const g of goods) {
    const prev = soldOutBySlot.get(g.slotNo);
    soldOutBySlot.set(g.slotNo, (prev ?? true) && g.soldOut); // 그룹 내 every(soldOut)
  }
  return [...soldOutBySlot.values()].some(Boolean); // 하나라도 전량품절 그룹 존재
};

const isAllFulfillment = (
  fulfillments:
    | GetSeedingRecruitmentDetailResponseData['fulfillments']
    | GetSeedingProposalDetailResponseData['fulfillments'],
  status: 'SHIPPING_READY' | 'DELIVERED',
): boolean => fulfillments.length > 0 && fulfillments.every((f) => f.status === status);

/**
 * 시딩 캠페인 (모집형) 상세 — 문서 "화면 상태 결정 로직" 11종을 raw 응답 조합으로 도출.
 *
 * - participation = null → BEFORE_APPLY / SOLD_OUT_UNAVAILABLE
 * - PENDING → recruitmentEndAt 기준으로 APPLIED / AWAITING_SELECTION
 * - CONFIRMED → fulfillments 의 최저 단계로 SELECTED_PREPARING / SHIPPING / DELIVERED
 * - COMPLETED / NOT_SELECTED / EXPIRED → 1:1 매핑
 * - SELF_WITHDRAWN(자진취소) → 모집기간 내면 BEFORE_APPLY(신청 화면 복귀, 단 재신청 불가=신청 CTA 비활성 SID-4366), 종료 후면 SELF_WITHDRAWN
 *
 * 미정의 조합 (예: campaign.status = INACTIVE) 은 BEFORE_APPLY 로 fallback —
 * 화면이 깨지지 않도록 안전한 기본값으로 처리하고, FE 가 별도 노티/에러 화면을 띄울 수 있도록 한다.
 */
export const determineSeedingRecruitmentScreenState = (
  data: GetSeedingRecruitmentDetailResponseData,
  now: number,
): SeedingRecruitmentScreenState => {
  const { campaign, goods, participation, fulfillments, contentSubmissions } = data;

  if (participation === null) {
    if (isAllSoldOut(goods)) return SEEDING_RECRUITMENT_SCREEN_STATE.SOLD_OUT_UNAVAILABLE;
    return SEEDING_RECRUITMENT_SCREEN_STATE.BEFORE_APPLY;
  }

  switch (participation.status) {
    case 'PENDING': {
      const recruitmentEndAt = parseKstDateTime(campaign.recruitmentEndAt);
      return now < recruitmentEndAt
        ? SEEDING_RECRUITMENT_SCREEN_STATE.APPLIED
        : SEEDING_RECRUITMENT_SCREEN_STATE.AWAITING_SELECTION;
    }
    case 'CONFIRMED': {
      // 선정 직후 0원 주문/이행 생성 전이면 fulfillments 가 비어있음 → 상품 준비중.
      // (빈 배열을 isAllFulfillment 가 false 처리해 배송중으로 떨어지던 것 보정)
      if (fulfillments.length === 0) return SEEDING_RECRUITMENT_SCREEN_STATE.SELECTED_PREPARING;
      if (isAllFulfillment(fulfillments, 'DELIVERED'))
        return SEEDING_RECRUITMENT_SCREEN_STATE.DELIVERED;
      if (isAllFulfillment(fulfillments, 'SHIPPING_READY'))
        return SEEDING_RECRUITMENT_SCREEN_STATE.SELECTED_PREPARING;
      return SEEDING_RECRUITMENT_SCREEN_STATE.SHIPPING;
    }
    case 'COMPLETED':
      return contentSubmissions.length > 0
        ? SEEDING_RECRUITMENT_SCREEN_STATE.CONTENT_SUBMITTED
        : SEEDING_RECRUITMENT_SCREEN_STATE.DELIVERED;
    case 'NOT_SELECTED':
      return SEEDING_RECRUITMENT_SCREEN_STATE.NOT_SELECTED;
    case 'SELF_WITHDRAWN': {
      // 자진취소 후 모집 기간이 아직 열려 있으면 신청 화면(BEFORE_APPLY)으로 복귀시킨다.
      // 단 취소한 캠페인은 재신청 불가 — 어댑터(mapRecruitmentDetailToView)가 신청 CTA 를
      // 비활성(disabled)으로 렌더한다. (SID-4366 — SID-4182 대응1의 '재신청 가능' 정책 반전)
      // 기간 종료 후엔 종료 화면(SELF_WITHDRAWN) 유지.
      const recruitmentEndAt = parseKstDateTime(campaign.recruitmentEndAt);
      if (now < recruitmentEndAt) {
        if (isAllSoldOut(goods)) return SEEDING_RECRUITMENT_SCREEN_STATE.SOLD_OUT_UNAVAILABLE;
        return SEEDING_RECRUITMENT_SCREEN_STATE.BEFORE_APPLY;
      }
      return SEEDING_RECRUITMENT_SCREEN_STATE.SELF_WITHDRAWN;
    }
    case 'EXPIRED':
      return SEEDING_RECRUITMENT_SCREEN_STATE.CONTENT_EXPIRED;
    default:
      // REJECTED / AUTO_TIMEOUT 는 제안형 전용. 모집형 응답엔 오지 않음.
      return SEEDING_RECRUITMENT_SCREEN_STATE.BEFORE_APPLY;
  }
};

/**
 * 시딩 캠페인 (제안형) 상세 — 화면 상태 7종을 raw 응답 조합으로 도출.
 *
 * - PENDING + 한 상품그룹(slotNo)이라도 전량 품절 → SOLD_OUT_UNAVAILABLE
 *   (그룹 내 일부 색상만 품절이면 그 옵션만 제외하고 진행. 다중 그룹 中 한 그룹 전량품절이면 진행불가)
 * - PENDING + optionsSelected = false → PENDING
 * - PENDING + optionsSelected = true → PRODUCT_SELECTED
 * - CONFIRMED → fulfillments 의 최저 단계로 PREPARING / SHIPPING / DELIVERED
 * - COMPLETED → contentSubmissions 유무로 CONTENT_SUBMITTED / DELIVERED
 * - REJECTED / AUTO_TIMEOUT / SELF_WITHDRAWN / EXPIRED 등 미정의 종료 상태는 PENDING 로 fallback —
 *   FE 가 별도 노티/에러 화면을 띄울 수 있도록 화면이 깨지지 않게 함.
 */
export const determineSeedingProposalScreenState = (
  data: GetSeedingProposalDetailResponseData,
): SeedingProposalScreenState => {
  const { goods, participation, fulfillments, contentSubmissions } = data;

  switch (participation.status) {
    case 'PENDING': {
      // 그룹(slotNo) 단위: 한 그룹이라도 전량품절이면 진행불가. 그룹 내 일부 색상 품절은 해당 옵션만 제외하고 진행.
      // (이전엔 isAnySoldOut 으로 단일 그룹의 1색 품절이 전체 진행불가로 처리되던 버그)
      if (isAnySlotGroupSoldOut(goods)) return SEEDING_PROPOSAL_SCREEN_STATE.SOLD_OUT_UNAVAILABLE;
      return participation.optionsSelected
        ? SEEDING_PROPOSAL_SCREEN_STATE.PRODUCT_SELECTED
        : SEEDING_PROPOSAL_SCREEN_STATE.PENDING;
    }
    case 'CONFIRMED': {
      // 수락 직후 0원 주문/이행 생성 전이면 fulfillments 가 비어있음 → 상품 준비중.
      if (fulfillments.length === 0) return SEEDING_PROPOSAL_SCREEN_STATE.PREPARING;
      if (isAllFulfillment(fulfillments, 'DELIVERED'))
        return SEEDING_PROPOSAL_SCREEN_STATE.DELIVERED;
      if (isAllFulfillment(fulfillments, 'SHIPPING_READY'))
        return SEEDING_PROPOSAL_SCREEN_STATE.PREPARING;
      return SEEDING_PROPOSAL_SCREEN_STATE.SHIPPING;
    }
    case 'COMPLETED':
      return contentSubmissions.length > 0
        ? SEEDING_PROPOSAL_SCREEN_STATE.CONTENT_SUBMITTED
        : SEEDING_PROPOSAL_SCREEN_STATE.DELIVERED;
    case 'REJECTED':
    case 'AUTO_TIMEOUT':
    case 'EXPIRED':
      // 거절 + 응답 기한 만료 자동 종료(AUTO_TIMEOUT/EXPIRED) — 모두 '미선정' 읽기전용 종료 화면.
      // 응답 기한 만료도 결과적으로 미선정이므로 REJECTED 와 동일 화면으로 보낸다. PENDING fallback 금지:
      // 종료된 제안이 '수락 대기'로 위장돼 거절 활성→409, 품절 분기 누락되던 결함 차단. (NOTICKET autotimeout)
      // (SID-4210, Figma 2276-34571 미선정 구조)
      return SEEDING_PROPOSAL_SCREEN_STATE.REJECTED;
    default:
      // SELF_WITHDRAWN 등 그 외 — 별도 종료 화면 정의 시까지 안전한 fallback (기획 확정 대기).
      return SEEDING_PROPOSAL_SCREEN_STATE.PENDING;
  }
};
